/**
 * Temu 发布记录展示态：
 * 1) 本地核价单 orderStatus（若可用）
 * 2) 平台货品生命周期 bg.glo.product.search（selectStatus / applyJitStatus）
 *
 * 核价 orderStatus：0 待核价 / 1 待供应商确认 / 2 核价通过 / 3 核价驳回 / 4 废弃 / 5 价格同步中
 * → 发布记录：待商家确认 / 待审核 / 已通过 / 已驳回
 */

export const PUBLISH_RECORD_STATUS = {
  PENDING_REVIEW: '待审核',
  PENDING_MERCHANT: '待商家确认',
  APPROVED: '已通过',
  REJECTED: '已驳回',
};

export const PUBLISH_RECORD_STATUS_TABS = [
  { id: 'all', label: '全部', status: '' },
  { id: '待商家确认', label: '待商家确认', status: '待商家确认' },
  { id: '待审核', label: '待审核', status: '待审核' },
  { id: '已通过', label: '已通过', status: '已通过' },
  { id: '已驳回', label: '已驳回', status: '已驳回' },
];

/** 单条核价 orderStatus → 对发布记录的影响；无法判定时返回 null */
export function reviewOrderStatusToPublishLabel(orderStatus) {
  const n = Number(orderStatus);
  if (!Number.isFinite(n)) return null;
  if (n === 0 || n === 1) return PUBLISH_RECORD_STATUS.PENDING_MERCHANT;
  if (n === 2) return PUBLISH_RECORD_STATUS.APPROVED;
  if (n === 3 || n === 4) return PUBLISH_RECORD_STATUS.REJECTED;
  return null;
}

/**
 * @param {Array<string|number|null|undefined>} statusCodes 该记录/SKC 命中的核价 orderStatus 列表；空=核价单从未出现
 */
export function derivePublishStatusFromReviewStatuses(statusCodes = []) {
  const nums = (Array.isArray(statusCodes) ? statusCodes : [])
    .map((v) => Number(v))
    .filter((n) => Number.isFinite(n));
  if (!nums.length) return PUBLISH_RECORD_STATUS.PENDING_REVIEW;
  if (nums.some((n) => n === 0 || n === 1)) return PUBLISH_RECORD_STATUS.PENDING_MERCHANT;
  if (nums.some((n) => n === 3 || n === 4)) return PUBLISH_RECORD_STATUS.REJECTED;
  if (nums.some((n) => n === 2)) return PUBLISH_RECORD_STATUS.APPROVED;
  return PUBLISH_RECORD_STATUS.PENDING_REVIEW;
}

export function resolvePublishStatusTone(status) {
  const s = String(status || '').trim();
  if (s === PUBLISH_RECORD_STATUS.APPROVED) return 'success';
  if (s === PUBLISH_RECORD_STATUS.REJECTED || s.includes('驳回')) return 'failed';
  return 'pending';
}

/** 从核价缓存行收集可用于匹配的 productSkuId */
export function collectReviewSkuIds(reviewRow) {
  const ids = new Set();
  const push = (v) => {
    const n = Number(v);
    if (Number.isSafeInteger(n) && n > 0) ids.add(String(n));
    const t = String(v ?? '').trim();
    if (t && t !== '0') ids.add(t);
  };
  if (!reviewRow || typeof reviewRow !== 'object') return ids;
  push(reviewRow.skuId);
  push(reviewRow.productSkuId);
  const list = reviewRow.productSkuIdList || reviewRow.productSkuIds;
  if (Array.isArray(list)) list.forEach(push);
  return ids;
}

/** 从发布记录 / SKC 行收集可匹配的 sku id */
export function collectRecordSkuIds(recordOrSkc) {
  const ids = new Set();
  const push = (v) => {
    const n = Number(v);
    if (Number.isSafeInteger(n) && n > 0) ids.add(String(n));
    const t = String(v ?? '').trim();
    if (t && t !== '0') ids.add(t);
  };
  if (!recordOrSkc || typeof recordOrSkc !== 'object') return ids;
  push(recordOrSkc.productSkuId);
  push(recordOrSkc.skuId);
  const list = recordOrSkc.productSkuIds || recordOrSkc.productSkuIdList;
  if (Array.isArray(list)) list.forEach(push);
  const merchants = recordOrSkc.merchantSkus;
  if (Array.isArray(merchants)) {
    merchants.forEach((m) => {
      push(m?.productSkuId);
      push(m?.skuId);
    });
  }
  const skcItems = recordOrSkc.skcItems;
  if (Array.isArray(skcItems)) {
    skcItems.forEach((skc) => {
      collectRecordSkuIds(skc).forEach((id) => ids.add(id));
    });
  }
  return ids;
}

/**
 * @param {Map<string, number>|Record<string, number>} skuStatusMap skuId → orderStatus
 * @param {Iterable<string>} skuIds
 */
export function lookupReviewStatusesForSkus(skuStatusMap, skuIds) {
  const out = [];
  const map = skuStatusMap instanceof Map
    ? skuStatusMap
    : new Map(Object.entries(skuStatusMap || {}));
  for (const id of skuIds || []) {
    const key = String(id);
    if (!map.has(key)) continue;
    out.push(map.get(key));
  }
  return out;
}

export function buildSkuReviewStatusMap(reviewItems = []) {
  const map = new Map();
  (Array.isArray(reviewItems) ? reviewItems : []).forEach((row) => {
    const status = Number(row?.orderStatus ?? row?.status);
    if (!Number.isFinite(status)) return;
    collectReviewSkuIds(row).forEach((id) => {
      const prev = map.get(id);
      // 同一 sku 多条：优先保留「待确认」类，其次驳回，再通过
      if (prev == null) {
        map.set(id, status);
        return;
      }
      const rank = (n) => {
        if (n === 0 || n === 1) return 3;
        if (n === 3 || n === 4) return 2;
        if (n === 2) return 1;
        return 0;
      };
      if (rank(status) >= rank(prev)) map.set(id, status);
    });
  });
  return map;
}

/** 给记录及其 skcItems 写入派生 status / statusLabel */
export function applyDerivedPublishStatuses(record, skuStatusMap) {
  if (!record || typeof record !== 'object') return record;
  const skcItems = Array.isArray(record.skcItems) ? record.skcItems : [];
  const nextSkc = skcItems.map((skc) => {
    const codes = lookupReviewStatusesForSkus(skuStatusMap, collectRecordSkuIds(skc));
    const label = derivePublishStatusFromReviewStatuses(codes);
    return {
      ...skc,
      status: label,
      statusLabel: label,
      discussStatus: label === PUBLISH_RECORD_STATUS.PENDING_MERCHANT ? 1 : undefined,
    };
  });
  const recordCodes = lookupReviewStatusesForSkus(skuStatusMap, collectRecordSkuIds({
    ...record,
    skcItems: nextSkc,
  }));
  // 记录级：有 SKC 时汇总 SKC 态；否则用整单 sku
  let label;
  if (nextSkc.length) {
    const labels = nextSkc.map((s) => s.statusLabel);
    if (labels.includes(PUBLISH_RECORD_STATUS.PENDING_MERCHANT)) {
      label = PUBLISH_RECORD_STATUS.PENDING_MERCHANT;
    } else if (labels.includes(PUBLISH_RECORD_STATUS.REJECTED)) {
      label = PUBLISH_RECORD_STATUS.REJECTED;
    } else if (labels.length && labels.every((s) => s === PUBLISH_RECORD_STATUS.APPROVED)) {
      label = PUBLISH_RECORD_STATUS.APPROVED;
    } else if (labels.includes(PUBLISH_RECORD_STATUS.APPROVED)
      && labels.every((s) => s === PUBLISH_RECORD_STATUS.APPROVED || s === PUBLISH_RECORD_STATUS.PENDING_REVIEW)) {
      label = PUBLISH_RECORD_STATUS.PENDING_REVIEW;
    } else {
      label = derivePublishStatusFromReviewStatuses(recordCodes);
    }
  } else {
    label = derivePublishStatusFromReviewStatuses(recordCodes);
  }
  return {
    ...record,
    skcItems: nextSkc,
    status: label,
    statusLabel: label,
  };
}
